
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { PropType } from 'vue'
import type { IndexClientDTO, IndexClientFeedDTO } from '../../models'
import type { IndexJobServiceListFilters } from '../../services/interfaces'

import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from 'vue'

import { mapActions, mapGetters } from '../../composables'
import { FilterTypeEnum } from '../../enums'
import { capitalizeSentence } from '../../filters'

import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { INDEX_CONSTS } from '../../store/index/index.consts'

import { UiButton, UiModal } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import { EpbSelect } from '../shared/epb-select'

interface Option {
  value: string
  label: string
}

interface FilterOption {
  label: string
  values: Array<Option>
}

interface InstanceFilter {
  id: string
  type: FilterTypeEnum
  values: Array<{ id: string; prettyName?: string }>
}

export default defineComponent({
  name: 'IndexUpdateJobConfigForm',
  components: {
    EpbSelect,
    EpbButton,
    UiModal,
    UiButton,
  },
  props: {
    instances: {
      type: Array as PropType<Array<Instance>>,
      required: true,
    },
    filters: {
      type: Object as PropType<IndexJobServiceListFilters>,
      required: true,
    },
  },
  setup(props: { instances: Array<Instance>; filters: IndexJobServiceListFilters }, { emit }) {
    const baseClass = 'epb-index-update-job-config-form'

    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()

    const messages = $epb.messages.tools.index.configModal

    const { getClient, updateConfig } = mapActions($store, INDEX_CONSTS.moduleName, {
      getClient: INDEX_CONSTS.actions.getClient,
      updateConfig: INDEX_CONSTS.actions.updateConfig,
    })

    const { instances } = mapGetters<Array<Instance>>($store, CONFIG_CONSTS.moduleName, {
      instances: CONFIG_CONSTS.getters.instances,
    })

    const updateConfigModal = ref(null)

    const onOpenModal = (): void => {
      updateConfigModal.value.open()
    }

    const getOptionFromList = (item: Instance & IndexClientFeedDTO): Option => {
      return {
        label: item.prettyName || item.id,
        value: item.id,
      }
    }

    const optionsSort = (option1: Option, option2: Option): number => {
      return option1.label.localeCompare(option2.label)
    }

    const instancesOptions = computed<Array<Option>>(() => {
      return props.instances.map(getOptionFromList).sort(optionsSort)
    })

    const findInstanceOptionSelected = (option: Option): boolean => {
      if (!props.filters.clientIds?.length) {
        return false
      }
      return option.value === props.filters.clientIds[0]
    }

    const instance = ref<IndexClientDTO>()
    const instanceSelectedValue = ref<string>(
      instancesOptions.value.length === 1
        ? instancesOptions.value[0].value
        : instancesOptions.value.find(findInstanceOptionSelected)?.value || '',
    )
    const clientFeedSelectedValue = ref<string>('')

    const clientFeedIdsOptions = computed<Array<Option>>(() => {
      if (!instance.value) {
        return []
      }
      return instance.value.feeds
        .filter((feed: IndexClientFeedDTO) => {
          const feedId = (feed as IndexClientFeedDTO & { id: string }).id
          return feedId && !feedId.toLowerCase().includes('partial')
        })
        .map(getOptionFromList)
        .sort(optionsSort)
    })

    const validForm = computed<boolean>(() => {
      const hasInstance = instancesOptions.value.length === 1 || !!instanceSelectedValue.value
      const hasFeed = !!clientFeedSelectedValue.value
      return hasInstance && hasFeed
    })

    const selectedInstance = computed<Instance | undefined>(() => {
      if (!instanceSelectedValue.value) {
        return undefined
      }
      return instances.value.find(instance => instance.id === instanceSelectedValue.value)
    })

    const selectedInstanceFilters = computed<Array<FilterOption>>(() => {
      if (!selectedInstance.value?.filters) {
        return []
      }
      const filters = Object.values(selectedInstance.value.filters).filter(
        (filter: InstanceFilter) => filter.type === FilterTypeEnum.REGULAR,
      )
      return filters.map((filter: InstanceFilter) => ({
        label: filter.id,
        values: [
          { value: '', label: 'None' },
          ...filter.values.map(value => ({
            value: value.id,
            label: value.prettyName || value.id,
          })),
        ],
      }))
    })

    const initializeSelectedPivots = (): Record<string, string> => {
      const pivots: Record<string, string> = {}
      selectedInstanceFilters.value.forEach(filter => {
        pivots[filter.label] = ''
      })
      return pivots
    }

    const selectedPivots = ref<Record<string, string>>(initializeSelectedPivots())

    const onBeforeClose = (): boolean => {
      clientFeedSelectedValue.value = ''
      selectedPivots.value = initializeSelectedPivots()
      return true
    }

    const onAccept = async (): Promise<void> => {
      if (!instanceSelectedValue.value || !clientFeedSelectedValue.value) {
        return
      }

      try {
        await updateConfig({
          clientId: instanceSelectedValue.value,
          feedId: clientFeedSelectedValue.value,
          pivots: selectedPivots.value,
        })

        instanceSelectedValue.value = ''
        clientFeedSelectedValue.value = ''
        selectedPivots.value = initializeSelectedPivots()

        updateConfigModal.value?.close()
        emit('success', messages.success, true)
      } catch (error: unknown) {
        emit('error', error)
      }
    }

    const onCancel = (): void => {
      updateConfigModal.value.close()
    }

    const doGetInstance = async (): Promise<void> => {
      if (instanceSelectedValue.value) {
        instance.value = await getClient(instanceSelectedValue.value)
      }
    }

    onMounted((): void => {
      doGetInstance()
    })

    watch(instanceSelectedValue, () => {
      doGetInstance()
      selectedPivots.value = initializeSelectedPivots()
    })

    watch(
      selectedInstanceFilters,
      () => {
        const newPivots = initializeSelectedPivots()
        Object.keys(selectedPivots.value).forEach(key => {
          if (key in newPivots) {
            newPivots[key] = selectedPivots.value[key]
          }
        })
        selectedPivots.value = newPivots
      },
      { deep: true },
    )

    return {
      baseClass,
      messages,
      updateConfigModal,
      instancesOptions,
      instanceSelectedValue,
      clientFeedSelectedValue,
      validForm,
      clientFeedIdsOptions,
      selectedInstance,
      selectedInstanceFilters,
      selectedPivots,
      capitalizeSentence,
      onBeforeClose,
      onAccept,
      onCancel,
      onOpenModal,
    }
  },
})
